import '../sass/recipe-single.scss';

/**
 * Single recipe interactions: print, share popups, cook mode (screen wake lock).
 */
const RecipeSingle = {
	wakeLock: null,

	init() {
		this.bindPrint();
		this.bindShare();
		this.bindCookMode();
		this.bindVideo();
	},

	/**
	 * Click-to-play hero video: swap the poster + play button for the player
	 * iframe only on click (no third-party embed loads until requested).
	 */
	bindVideo() {
		document.querySelectorAll( '.js-recipe-video' ).forEach( ( wrap ) => {
			const embed = wrap.dataset.embed;
			const play = wrap.querySelector( '.js-recipe-video-play' );

			if ( ! embed || ! play ) {
				return;
			}

			play.addEventListener( 'click', () => {
				const separator = embed.indexOf( '?' ) === -1 ? '?' : '&';
				const iframe = document.createElement( 'iframe' );
				iframe.src = `${ embed }${ separator }autoplay=1`;
				iframe.className = 'recipe-video__iframe';
				iframe.title = document.title;
				iframe.setAttribute(
					'allow',
					'autoplay; fullscreen; picture-in-picture; encrypted-media'
				);
				iframe.setAttribute( 'allowfullscreen', '' );
				wrap.replaceChildren( iframe );
			} );
		} );
	},

	bindPrint() {
		document.querySelectorAll( '.js-recipe-print' ).forEach( ( button ) => {
			button.addEventListener( 'click', () => window.print() );
		} );
	},

	bindShare() {
		document.querySelectorAll( '.js-recipe-share' ).forEach( ( link ) => {
			const network = link.dataset.network;

			// Instagram has no web share-a-link endpoint; where the device
			// supports the Web Share API with files we hand the OS share sheet
			// the feature image + URL (Instagram appears there if installed).
			// Otherwise the link falls back to opening the Brodo profile.
			if ( network === 'instagram' ) {
				this.bindNativeShare( link );
				return;
			}

			// Email uses its native mailto target.
			if ( network === 'email' ) {
				return;
			}

			link.addEventListener( 'click', ( event ) => {
				event.preventDefault();
				window.open(
					link.href,
					'recipe-share',
					'width=600,height=540,menubar=no,toolbar=no'
				);
			} );
		} );
	},

	/**
	 * Upgrade a share link to a native image share when the platform supports it.
	 *
	 * @param {HTMLAnchorElement} link Share link with data-share-* attributes.
	 */
	bindNativeShare( link ) {
		const imageUrl = link.dataset.shareImage;
		const shareUrl = link.dataset.shareUrl || link.href;
		const shareTitle = link.dataset.shareTitle || document.title;

		// No image to share, or no file-capable Web Share API (e.g. desktop):
		// leave the link untouched so it opens the Instagram profile.
		if ( ! imageUrl || typeof navigator.canShare !== 'function' ) {
			return;
		}

		// Pre-fetch the image so navigator.share() can run synchronously inside
		// the click gesture — iOS Safari rejects share() called after an awaited
		// fetch. sharedFile stays null if fetching or file-sharing is unsupported.
		let sharedFile = null;
		fetch( imageUrl )
			.then( ( response ) => {
				if ( ! response.ok ) {
					throw new Error( 'Share image request failed' );
				}
				return response.blob();
			} )
			.then( ( blob ) => {
				const file = new File( [ blob ], 'brodo-recipe.jpg', {
					type: blob.type || 'image/jpeg',
				} );
				if ( navigator.canShare( { files: [ file ] } ) ) {
					sharedFile = file;
				}
			} )
			.catch( () => {
				sharedFile = null;
			} );

		link.addEventListener( 'click', ( event ) => {
			// Not ready / unsupported — let the link open the profile instead.
			if ( ! sharedFile ) {
				return;
			}

			event.preventDefault();
			navigator
				.share( {
					files: [ sharedFile ],
					title: shareTitle,
					url: shareUrl,
				} )
				.catch( ( error ) => {
					if ( error && error.name !== 'AbortError' ) {
						window.open( link.href, '_blank', 'noopener' );
					}
				} );
		} );
	},

	bindCookMode() {
		const toggle = document.querySelector( '.js-cook-mode' );

		if ( ! toggle ) {
			return;
		}

		// Hide the control entirely where Wake Lock is unsupported.
		if ( ! ( 'wakeLock' in navigator ) ) {
			const wrapper = toggle.closest( '.recipe-cookmode' );
			if ( wrapper ) {
				wrapper.style.display = 'none';
			}
			return;
		}

		toggle.addEventListener( 'change', async () => {
			document.body.classList.toggle( 'cook-mode-on', toggle.checked );

			if ( toggle.checked ) {
				try {
					this.wakeLock =
						await navigator.wakeLock.request( 'screen' );
				} catch ( error ) {
					this.wakeLock = null;
				}
			} else if ( this.wakeLock ) {
				this.wakeLock.release();
				this.wakeLock = null;
			}
		} );

		document.addEventListener( 'visibilitychange', async () => {
			if ( toggle.checked && document.visibilityState === 'visible' ) {
				try {
					this.wakeLock =
						await navigator.wakeLock.request( 'screen' );
				} catch ( error ) {
					this.wakeLock = null;
				}
			}
		} );
	},
};

document.addEventListener( 'DOMContentLoaded', () => RecipeSingle.init() );
